/**
 * 文言辞書（FR-I18N）。
 * 組み込み辞書に利用者の差分を重ね、未定義キーは英語へフォールバックする。
 */
import type { I18nOptions } from '../core/options';

type Dictionary = Record<string, string>;

const EN: Dictionary = {
  'viewer.label': 'Flipbook viewer',
  'viewer.surface': 'Book pages',
  'page.current': 'Page {page} of {total}',
  'page.label': 'Page {label}',
  'page.total': '/ {total}',
  'toolbar.label': 'Viewer controls',
  'toolbar.first': 'First page',
  'toolbar.prev': 'Previous page',
  'toolbar.next': 'Next page',
  'toolbar.last': 'Last page',
  'toolbar.zoomIn': 'Zoom in',
  'toolbar.zoomOut': 'Zoom out',
  'toolbar.resetZoom': 'Reset zoom',
  'toolbar.thumbnails': 'Thumbnails',
  'toolbar.outline': 'Table of contents',
  'toolbar.search': 'Search',
  'toolbar.download': 'Download PDF',
  'toolbar.print': 'Print',
  'toolbar.fullscreen': 'Fullscreen',
  'toolbar.lightbox': 'Expand',
  'toolbar.sound': 'Turn page sound on',
  'toolbar.soundOff': 'Turn page sound off',
  'toolbar.pageInput': 'Page number',
  'panel.close': 'Close panel',
  'panel.thumbnails': 'Thumbnails',
  'panel.outline': 'Table of contents',
  'panel.search': 'Search',
  'search.label': 'Search in this book',
  'search.placeholder': 'Search…',
  'search.searching': 'Searching…',
  'search.results': '{count} results',
  'search.noResults': 'No results',
  'search.minChars': 'Enter at least {count} characters',
  'search.page': 'Page {page}',
  'panel.share': 'Share',
  'toolbar.share': 'Share',
  'share.url': 'Link to this view',
  'share.copy': 'Copy',
  'share.copied': 'Copied to clipboard',
  'share.copyManual': 'Could not copy automatically. The text is selected — press Ctrl/Cmd+C.',
  'share.embed': 'Embed code',
  'share.qrLabel': 'QR code for the share link',
  'share.qrTooLong': 'The link is too long for a QR code.',
  'share.linkTitle': 'Page {page}',
  'unlock.title': 'Password required',
  'unlock.description': 'This book is password protected.',
  'unlock.password': 'Password',
  'unlock.submit': 'View',
  'unlock.invalid': 'The password is incorrect.',
  'unlock.failed': 'Could not check the password. Please try again.',
  // 遮断ページ（EP-5）。既定は「会員限定」で、サーバが理由を添えたときだけ差し替わる。
  'locked.title': 'Members only',
  'locked.hint': 'Sign in to keep reading',
  'locked.gate.title': 'Form required',
  'locked.gate.hint': 'Submit the form in this book to keep reading',
  'locked.password.title': 'Password required',
  'locked.password.hint': 'These pages are password protected',
  'locked.schedule.title': 'Outside the publication period',
  'locked.schedule.hint': 'These pages are not available right now',
  'locked.group.title': 'Not available to you',
  'locked.group.hint': 'Your membership does not cover these pages',
  'locked.notice': '{title}. {hint}',
  'resume.notice': 'Resumed from page {page}',
  'loading.label': 'Loading…',
  'toolbar.help': 'How to use',
  'toolbar.speech': 'Read aloud',
  'panel.speech': 'Read aloud',
  'speech.play': 'Read this page',
  'speech.stop': 'Stop reading',
  'speech.rate': 'Speed',
  'speech.voice': 'Voice',
  'speech.voice.auto': 'Browser default',
  'speech.none': 'This browser has no speech voices installed, so reading aloud is unavailable here.',
  'speech.otherLang': 'No voice for this book\u2019s language ({lang}) is installed. The browser default voice will be used.',
  'speech.empty': 'There is no body text to read on this page.',
  'panel.help': 'How to use',
  'help.keyboard': 'Keyboard',
  'help.touch': 'Touch',
  'help.key.flipRight': 'Page on the right',
  'help.key.flipLeft': 'Page on the left',
  'help.key.next': 'Next page',
  'help.key.prev': 'Previous page',
  'help.key.first': 'First page',
  'help.key.last': 'Last page',
  'help.key.zoomIn': 'Zoom in',
  'help.key.zoomOut': 'Zoom out',
  'help.key.resetZoom': 'Reset zoom',
  'help.key.help': 'Open or close this help',
  'help.key.pan': 'Move the view while zoomed in',
  'help.key.close': 'Close the open panel or lightbox',
  'help.touch.swipe': 'Swipe to turn the page',
  'help.touch.pinch': 'Pinch to zoom in or out',
  'help.touch.doubleTap': 'Double-tap to zoom in or reset',
  'toolbar.marks': 'Bookmarks and tabs',
  'toolbar.bookmark': 'Bookmark this page',
  'toolbar.sticky': 'Tab this page',
  'toolbar.shelf': 'Add to my shelf',
  'panel.marks': 'Bookmarks and tabs',
  'marks.empty': 'No bookmarks yet',
  'marks.page': 'Page {page}',
  'marks.bookmark': 'Bookmark',
  'marks.sticky': 'Tab',
  'marks.remove': 'Remove',
  'shelf.empty': 'Your shelf is empty',
  'history.empty': 'No reading history yet',
  'history.page': 'Page {page}',
  'toolbar.comments': 'Comments',
  'toolbar.like': 'Like this book',
  'toolbar.likeSignIn': 'Sign in to like this book',
  // 件数はボタンの中に見えているので、読み上げ名にも同じ数字を含める（WCAG 2.5.3）。
  'toolbar.likeCount': 'Like this book ({count})',
  'toolbar.likeSignInCount': 'Sign in to like this book ({count})',
  'panel.comments': 'Comments',
  'comments.empty': 'No comments yet',
  'comments.loading': 'Loading…',
  'comments.label': 'Your comment',
  'comments.placeholder': 'Write a comment',
  'comments.submit': 'Post',
  'comments.signIn': 'Sign in to post a comment.',
  'comments.pending': 'Thanks! Your comment will appear once it is approved.',
  'comments.published': 'Your comment has been posted.',
  'comments.failed': 'Could not post your comment. Please try again later.',
  'comments.anonymous': 'Reader',
  'overlay.link.page': 'Go to page {page}',
  'overlay.link.book': 'Open {book}, page {page}',
  'overlay.link.external': 'Open {url}',
  'overlay.video': 'Video',
  'overlay.audio': 'Audio',
  'overlay.play': 'Play',
  'pan.up': 'Move the view up',
  'pan.down': 'Move the view down',
  'pan.left': 'Move the view left',
  'pan.right': 'Move the view right',
  'overlay.note.open': 'Open media',
  'overlay.close': 'Close',
  'overlay.form.submit': 'Submit',
  'overlay.form.sending': 'Sending…',
  'overlay.form.done': 'Thank you. Your response has been sent.',
  'overlay.form.error': 'Could not send. Please try again.',
  'overlay.form.open': 'Open form',
  // 必須の可視表示（WCAG 3.3.2）と、誤りの特定・直し方の提示（3.3.1・3.3.3）。
  'overlay.form.required': 'required',
  'overlay.form.errorRequired': 'Enter {label}.',
  'overlay.form.errorRequiredCheck': 'Select {label}.',
  'overlay.form.errorRequiredCheckbox': 'Check {label}.',
  'overlay.form.errorEmail': 'Enter {label} as an email address, for example name@example.com.',
  'overlay.form.errorFormat': 'Check the format of {label}.',
  'overlay.form.errorSummary': '{count} field(s) need attention. Moved to the first one.',
  // 解錠の反映は利用者の操作で行う（WCAG 2.2.1）。
  'overlay.form.unlocked': 'The rest of the book is now unlocked.',
  'overlay.form.unlockApply': 'Show the unlocked pages',
};

const JA: Dictionary = {
  'viewer.label': 'フリップブックビューワ',
  'viewer.surface': '誌面',
  'page.current': '{total} ページ中 {page} ページ目',
  'page.label': '{label} ページ',
  'page.total': '/ {total}',
  'toolbar.label': 'ビューワ操作',
  'toolbar.first': '最初のページ',
  'toolbar.prev': '前のページ',
  'toolbar.next': '次のページ',
  'toolbar.last': '最後のページ',
  'toolbar.zoomIn': '拡大',
  'toolbar.zoomOut': '縮小',
  'toolbar.resetZoom': '等倍に戻す',
  'toolbar.thumbnails': 'サムネイル',
  'toolbar.outline': '目次',
  'toolbar.search': '検索',
  'toolbar.download': 'PDF をダウンロード',
  'toolbar.print': '印刷',
  'toolbar.fullscreen': '全画面',
  'toolbar.lightbox': '拡大表示',
  'toolbar.sound': 'めくり音を有効にする',
  'toolbar.soundOff': 'めくり音を無効にする',
  'toolbar.pageInput': 'ページ番号',
  'panel.close': 'パネルを閉じる',
  'panel.thumbnails': 'サムネイル',
  'panel.outline': '目次',
  'panel.search': '検索',
  'search.label': 'この本の中を検索',
  'search.placeholder': '検索…',
  'search.searching': '検索中…',
  'search.results': '{count} 件',
  'search.noResults': '該当なし',
  'search.minChars': '{count} 文字以上入力してください',
  'search.page': '{page} ページ',
  'panel.share': '共有',
  'toolbar.share': '共有',
  'share.url': 'この表示へのリンク',
  'share.copy': 'コピー',
  'share.copied': 'クリップボードにコピーしました',
  'share.copyManual': '自動コピーできませんでした。選択してあるので Ctrl/Cmd+C を押してください。',
  'share.embed': '埋め込みコード',
  'share.qrLabel': '共有リンクの QR コード',
  'share.qrTooLong': 'リンクが長すぎるため QR コードを表示できません。',
  'share.linkTitle': '{page} ページ',
  'unlock.title': 'パスワードが必要です',
  'unlock.description': 'この本の閲覧にはパスワードが必要です。',
  'unlock.password': 'パスワード',
  'unlock.submit': '閲覧する',
  'unlock.invalid': 'パスワードが違います。',
  'unlock.failed': 'パスワードを確認できませんでした。もう一度お試しください。',
  'locked.title': '会員限定',
  'locked.hint': '続きを読むにはログインしてください',
  'locked.gate.title': 'フォームの送信が必要です',
  'locked.gate.hint': '続きを読むにはこの本のフォームを送信してください',
  'locked.password.title': 'パスワードが必要です',
  'locked.password.hint': 'このページの閲覧にはパスワードが必要です',
  'locked.schedule.title': '公開期間外',
  'locked.schedule.hint': 'このページはいま公開されていません',
  'locked.group.title': '閲覧できません',
  'locked.group.hint': 'このページを読める会員種別ではありません',
  'locked.notice': '{title}。{hint}',
  'resume.notice': '{page} ページから再開しました',
  'loading.label': '読み込み中…',
  'toolbar.help': '操作ヘルプ',
  'toolbar.speech': '読み上げ',
  'panel.speech': '読み上げ',
  'speech.play': 'このページを読み上げる',
  'speech.stop': '読み上げを止める',
  'speech.rate': '速さ',
  'speech.voice': '音声',
  'speech.voice.auto': 'ブラウザの既定',
  'speech.none': 'この環境には読み上げの音声が入っていないため、読み上げは使えません。',
  'speech.otherLang': 'この本の言語（{lang}）の音声が入っていません。ブラウザの既定の音声で読み上げます。',
  'speech.empty': 'このページには読み上げる本文がありません。',
  'panel.help': '操作ヘルプ',
  'help.keyboard': 'キーボード',
  'help.touch': 'タッチ',
  'help.key.flipRight': '右側のページへ',
  'help.key.flipLeft': '左側のページへ',
  'help.key.next': '次のページ',
  'help.key.prev': '前のページ',
  'help.key.first': '最初のページ',
  'help.key.last': '最後のページ',
  'help.key.zoomIn': '拡大',
  'help.key.zoomOut': '縮小',
  'help.key.resetZoom': '等倍に戻す',
  'help.key.help': 'このヘルプを開く・閉じる',
  'help.key.pan': '拡大中に表示位置を動かす',
  'help.key.close': '開いているパネル・拡大表示を閉じる',
  'help.touch.swipe': 'スワイプでページをめくる',
  'help.touch.pinch': 'ピンチで拡大・縮小',
  'help.touch.doubleTap': 'ダブルタップで拡大・等倍に戻す',
  'toolbar.marks': 'しおり・ふせん',
  'toolbar.bookmark': 'このページにしおりを挟む',
  'toolbar.sticky': 'このページにふせんを貼る',
  'toolbar.shelf': '本棚に入れる',
  'panel.marks': 'しおり・ふせん',
  'marks.empty': 'しおり・ふせんはまだありません',
  'marks.page': '{page} ページ',
  'marks.bookmark': 'しおり',
  'marks.sticky': 'ふせん',
  'marks.remove': '外す',
  'shelf.empty': '本棚は空です',
  'history.empty': '閲覧履歴はまだありません',
  'history.page': '{page} ページまで',
  'toolbar.comments': 'コメント',
  'toolbar.like': 'いいね',
  'toolbar.likeSignIn': 'いいねするにはログインが必要です',
  'toolbar.likeCount': 'いいね {count} 件',
  'toolbar.likeSignInCount': 'いいねするにはログインが必要です（{count} 件）',
  'panel.comments': 'コメント',
  'comments.empty': 'コメントはまだありません',
  'comments.loading': '読み込み中…',
  'comments.label': 'コメント本文',
  'comments.placeholder': 'コメントを入力',
  'comments.submit': '投稿する',
  'comments.signIn': 'コメントするにはログインが必要です。',
  'comments.pending': 'コメントを受け付けました。承認後に公開されます。',
  'comments.published': 'コメントを公開しました。',
  'comments.failed': 'コメントを投稿できませんでした。時間をおいてお試しください。',
  'comments.anonymous': '読者',
  'overlay.link.page': '{page} ページへ移動',
  'overlay.link.book': '{book} の {page} ページを開く',
  'overlay.link.external': '{url} を開く',
  'overlay.video': '動画',
  'overlay.audio': '音声',
  'overlay.play': '再生',
  'pan.up': '表示位置を上へ',
  'pan.down': '表示位置を下へ',
  'pan.left': '表示位置を左へ',
  'pan.right': '表示位置を右へ',
  'overlay.note.open': 'メディアを開く',
  'overlay.close': '閉じる',
  'overlay.form.submit': '送信する',
  'overlay.form.sending': '送信中…',
  'overlay.form.done': '送信しました。ありがとうございました。',
  'overlay.form.error': '送信できませんでした。もう一度お試しください。',
  'overlay.form.open': 'フォームを開く',
  'overlay.form.required': '必須',
  'overlay.form.errorRequired': '{label}を入力してください。',
  'overlay.form.errorRequiredCheck': '{label}を選択してください。',
  'overlay.form.errorRequiredCheckbox': '{label}にチェックを入れてください。',
  'overlay.form.errorEmail': '{label}はメールアドレスの形式で入力してください（例: name@example.com）。',
  'overlay.form.errorFormat': '{label}の形式を確認してください。',
  'overlay.form.errorSummary': '{count} 件の入力に誤りがあります。最初の項目へ移動しました。',
  'overlay.form.unlocked': 'この先のページを解錠しました。',
  'overlay.form.unlockApply': '解錠したページを表示する',
};

const BUILT_IN: Record<string, Dictionary> = { en: EN, ja: JA };

/** 'auto' を文書・ブラウザの設定から解決する。 */
function resolveLocale(locale: string, doc: Document): string {
  if (locale && locale !== 'auto') return locale;
  const declared = doc.documentElement.lang || doc.defaultView?.navigator.language || 'en';
  return declared.toLowerCase().split('-')[0] ?? 'en';
}

export type Translate = (key: string, params?: Record<string, string | number>) => string;

/** 文言を引く関数を作る。優先順: 利用者の差分 → 組み込み（言語） → 組み込み（英語） → キー名。 */
export function createTranslator(options: I18nOptions, doc: Document): Translate {
  const locale = resolveLocale(options.locale, doc);
  const dictionary = BUILT_IN[locale] ?? EN;

  return (key, params) => {
    const template = options.strings[key] ?? dictionary[key] ?? EN[key] ?? key;
    if (!params) return template;
    return template.replace(/\{(\w+)\}/g, (match, name: string) =>
      name in params ? String(params[name]) : match,
    );
  };
}
